/**
 * Binary framing of the data chunks.
 *
 * Each chunk travels as an `ArrayBuffer` with a fixed-size header
 * followed by the payload — thus the receiver knows WHICH file and at WHICH offset
 * each piece belongs WITHOUT implicit order state (key to resuming after
 * a cut and to interleaving without ambiguity). The per-chunk integrity travels in
 * the header itself (CRC32) to be able to reject a corrupt frame on the spot.
 *
 * Header (16 bytes, big-endian):
 *   [0..4)   fileIndex  u32   file index within the manifest
 *   [4..12)  offset     f64   start byte in the file (safe up to 2^53)
 *   [12..16) crc32      u32   CRC32 of the payload
 * Payload: the file bytes from `offset`.
 */

import { crc32 } from "./hash";

export const CHUNK_HEADER_BYTES = 16;

export interface ChunkFrame {
  fileIndex: number;
  offset: number;
  payload: Uint8Array;
}

/** Serializes a chunk into an `ArrayBuffer` ready for `channel.send`. */
export function encodeChunk(fileIndex: number, offset: number, payload: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(CHUNK_HEADER_BYTES + payload.length);
  const view = new DataView(buffer);
  view.setUint32(0, fileIndex, false);
  view.setFloat64(4, offset, false);
  view.setUint32(12, crc32(payload), false);
  new Uint8Array(buffer, CHUNK_HEADER_BYTES).set(payload);
  return buffer;
}

/**
 * Parses and VERIFIES a binary frame. Returns `null` if the header does not fit
 * or the CRC does not match (corrupt/tampered frame) — the receiver treats it as
 * an integrity error, it never trusts the wire.
 */
export function decodeChunk(data: ArrayBuffer): ChunkFrame | null {
  if (data.byteLength < CHUNK_HEADER_BYTES) {
    return null;
  }
  const view = new DataView(data);
  const fileIndex = view.getUint32(0, false);
  const offset = view.getFloat64(4, false);
  const expectedCrc = view.getUint32(12, false);
  if (!Number.isInteger(offset) || offset < 0) {
    return null;
  }
  const payload = new Uint8Array(data.slice(CHUNK_HEADER_BYTES));
  if (crc32(payload) !== expectedCrc) {
    return null;
  }
  return { fileIndex, offset, payload };
}
